import { Check, ChevronDown, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { ColocaPopover } from "@/shared/components/overlays/ColocaPopover";
import { isOnboardingCountryIso2 } from "@/shared/data/onboarding-countries";
import { ALL_PHONE_COUNTRIES, type PhoneCountry } from "@/shared/data/phone-countries";
import { countryMatchesQuery, localizedCountryName } from "@/shared/data/phone-country-list";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

interface AccountCountrySelectProps {
  /** ISO 3166-1 alpha-2 code (e.g. `"CO"`). Empty when nothing selected. */
  value: string;
  onValueChange: (iso2: string) => void;
  disabled?: boolean;
  placeholder?: string;
  "aria-label"?: string;
}

function FlagIcon({ iso2, size = 16 }: { iso2: string | null; size?: number }) {
  if (!iso2) {
    return (
      <span
        aria-hidden="true"
        className="inline-grid shrink-0 place-items-center rounded-[2px] bg-muted text-[10px] text-muted-foreground"
        style={{ width: size + 6, height: size }}
      >
        🌐
      </span>
    );
  }
  return (
    <img
      src={`https://flagcdn.com/${iso2.toLowerCase()}.svg`}
      alt=""
      aria-hidden="true"
      loading="lazy"
      className="inline-block shrink-0 rounded-[2px] object-cover"
      style={{ width: size + 6, height: size }}
    />
  );
}

/**
 * Settings-scoped country picker. A clean dropdown trigger that opens a popover with a search box and a
 * scrollable flag-prefixed list of the countries we can onboard from. Kept local to the settings module so it
 * can evolve independently from the shared `ColocaCountrySelect` / `ColocaPhoneField`.
 *
 * An account saved before the allowlist existed keeps its stored country listed and selected — hiding it
 * would show a blank trigger over a value that is still on the record.
 */
export function AccountCountrySelect({
  value,
  onValueChange,
  disabled,
  placeholder,
  ...rest
}: AccountCountrySelectProps) {
  const { t, activeLanguage } = useTranslations("settings");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = useMemo<PhoneCountry | null>(() => {
    if (!value) return null;
    const upper = value.toUpperCase();
    const stored = ALL_PHONE_COUNTRIES.find((c) => c.iso2 === upper);
    return stored ? { ...stored, name: localizedCountryName(stored.iso2, activeLanguage) } : null;
  }, [value, activeLanguage]);

  // Named in the active language and sorted by that name: the catalog carries English names ordered by ISO
  // code, which reads as an unsorted list to anybody not using the app in English.
  const available = useMemo(() => {
    const collator = new Intl.Collator(activeLanguage, { sensitivity: "base" });
    const allowed = ALL_PHONE_COUNTRIES.filter((c) => isOnboardingCountryIso2(c.iso2))
      .map((c) => ({ ...c, name: localizedCountryName(c.iso2, activeLanguage) }))
      .sort((a, b) => collator.compare(a.name, b.name));
    if (!selected || allowed.some((c) => c.iso2 === selected.iso2)) return allowed;
    return [selected, ...allowed];
  }, [selected, activeLanguage]);

  // Shared with the phone field and `ColocaCountryField` — see the note there: the names are localized, so a
  // raw `includes` cannot find a country whose name carries a diacritic.
  const filtered = useMemo(
    () => (query.trim() ? available.filter((c) => countryMatchesQuery(c, query)) : available),
    [available, query],
  );

  return (
    <ColocaPopover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
      side="bottom"
      align="start"
      contentClassName="w-[var(--anchor-width)] min-w-[280px] p-0"
      trigger={
        <button
          type="button"
          disabled={disabled}
          aria-label={rest["aria-label"]}
          aria-expanded={open}
          className={cn(
            "flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-1 text-sm text-foreground shadow-xs transition-[color,box-shadow] outline-none",
            "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
            "aria-expanded:border-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          <FlagIcon iso2={selected?.iso2 ?? null} />
          <span
            className={cn(
              "flex-1 truncate text-left",
              selected ? "text-foreground" : "text-muted-foreground",
            )}
          >
            {selected?.name ?? placeholder ?? t("language_country_placeholder")}
          </span>
          <ChevronDown size={14} className="shrink-0 text-muted-foreground" aria-hidden />
        </button>
      }
    >
      <div className="flex flex-col">
        {/* Search */}
        <div className="relative border-b border-border p-2">
          <Search
            size={13}
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <ColocaInput
            autoFocus
            value={query}
            onValueChange={setQuery}
            placeholder={t("language_country_search_placeholder")}
            className="h-8 pl-7 text-sm"
          />
        </div>

        {/* List */}
        <ul role="listbox" className="max-h-[260px] overflow-y-auto overscroll-contain p-1">
          {filtered.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-muted-foreground">
              {t("language_country_no_results")}
            </li>
          ) : (
            filtered.map((c) => {
              const isSelected = selected?.iso2 === c.iso2;
              return (
                <li key={c.iso2}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => {
                      onValueChange(c.iso2);
                      setOpen(false);
                      setQuery("");
                    }}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors",
                      isSelected
                        ? "bg-accent text-accent-foreground"
                        : "text-foreground hover:bg-foreground/5",
                    )}
                  >
                    <FlagIcon iso2={c.iso2} />
                    <span className="flex-1 truncate">{c.name}</span>
                    {isSelected && <Check size={14} className="shrink-0 text-primary" aria-hidden />}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </ColocaPopover>
  );
}
